import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./Journal.css";
import { Edit, Trash } from "lucide-react";

const API_URL = "https://payyug.in/api";

const Journal = () => {
  const [branches, setBranches] = useState([]);
  const [branchLedgers, setBranchLedgers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedLedger, setSelectedLedger] = useState(null);

  const [entries, setEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);

  const [formData, setFormData] = useState({
    voucherType: "journal",
    date: "",
    narration: "",
    amount: "",
    entryType: "",
  });

  /* ================= FETCH BRANCHES ================= */
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get(`${API_URL}/get-branch`, {
          params: {
            SuperAdminId: localStorage.getItem("adminuserId"),
            limit: "all",
          },
        });
        console.log("branch:",res.data)
        setBranches(res?.data?.branches || []);
      } catch {
        toast.error("Failed to load branches");
      }
    };

    fetchBranches();
  }, []);

  /* ================= FETCH LEDGERS BY BRANCH ================= */
  useEffect(() => {
    if (!selectedBranch) {
      setBranchLedgers([]);
      return;
    }

    const fetchBranchLedgers = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/branch-ledger/get-all`,
          {
            params: {
              superAdminId: localStorage.getItem("adminuserId"),
              branch_id: selectedBranch.id,
            },
          }
        );
        console.log("branch ledger",res.data.data)
        setBranchLedgers(res?.data?.data || []);
      } catch {
        toast.error("Failed to load ledgers");
      }
    };

    fetchBranchLedgers();
  }, [selectedBranch]);

  /* ================= ADD / UPDATE ENTRY ================= */
  const handleAddOrUpdateEntry = () => {
    if (!selectedBranch) {
      toast.error("Select branch");
      return;
    }

    if (!selectedLedger) {
      toast.error("Select ledger");
      return;
    }

    if (!formData.entryType) {
      toast.error("Select Debit or Credit");
      return;
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error("Enter valid amount");
      return;
    }

    const entry = {
      ledgerId: selectedLedger.id,
      ledgerName: selectedLedger.ledger.name,
      ledgerType: selectedLedger.ledger.type,
      debit: formData.entryType === "debit" ? Number(formData.amount) : 0,
      credit: formData.entryType === "credit" ? Number(formData.amount) : 0,
    };

    if (editingIndex !== null) {
      const updated = [...entries];
      updated[editingIndex] = entry;
      setEntries(updated);
      setEditingIndex(null);
    } else {
      setEntries((prev) => [...prev, entry]);
    }

    setSelectedLedger(null);
    setFormData((p) => ({ ...p, amount: "", entryType: "" }));
  };

  /* ================= EDIT ENTRY ================= */
  const handleEdit = (entry, index) => {
    setEditingIndex(index);
    setFormData((p) => ({
      ...p,
      amount: entry.debit || entry.credit,
      entryType: entry.debit > 0 ? "debit" : "credit",
    }));

    const ledger = branchLedgers.find((l) => l.id === entry.ledgerId);
    setSelectedLedger(ledger);
  };

  /* ================= DELETE ENTRY ================= */
  const handleDelete = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
    setEditingIndex(null);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBranch) {
      toast.error("Select branch");
      return;
    }

    if (entries.length < 2) {
      toast.error("Minimum 2 entries required");
      return;
    }

    const totalDebit = entries.reduce((a, e) => a + e.debit, 0);
    const totalCredit = entries.reduce((a, e) => a + e.credit, 0);

    if (totalDebit !== totalCredit) {
      toast.error("Debit and Credit must be equal");
      return;
    }

    const adminId = Number(localStorage.getItem("adminuserId"));

    const payload = {
      voucherType: "journal",
      date: formData.date,
      narration: formData.narration,
      branchId: selectedBranch.id,
      superAdminId: adminId,
      createdBy: adminId,
      entries,
    };

    try {
      await axios.post(
        `${API_URL}/ledger-entry/create-journal-voucher`,
        payload
      );

      toast.success("Journal Voucher Created");

      setEntries([]);
      setEditingIndex(null);
      setFormData({
        voucherType: "journal",
        date: "",
        narration: "",
        amount: "",
        entryType: "",
      });
      setSelectedLedger(null);
      setSelectedBranch(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create journal");
    }
  };

  return (
    <div className="journal-container">
      <h1 className="journal-heading">Journal Voucher</h1>

      <form className="journal-form" onSubmit={handleSubmit}>
        <div className="journal-form-section">
          <div className="journal-form-input-fields">
            <label>Branch</label>
            <select
              value={selectedBranch?.id || ""}
              onChange={(e) =>
                setSelectedBranch(
                  branches.find((b) => b.id === Number(e.target.value))
                )
              }
            >
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="journal-form-input-fields">
            <label>Date</label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
            />
          </div>

          <div className="journal-form-input-fields">
            <label>Voucher Type</label>
            <input value="Journal" style={{fontWeight:"600"}} disabled />
          </div>

          <div className="journal-form-input-fields">
            <label>Entry Type</label>
            <select
              value={formData.entryType}
              onChange={(e) =>
                setFormData({ ...formData, entryType: e.target.value })
              }
            >
              <option value="">Debit / Credit</option>
              <option value="debit">Debit</option>
              <option value="credit">Credit</option>
            </select>
          </div>

          <div className="journal-form-input-fields">
            <label>Ledger</label>
            <select
              disabled={!selectedBranch}
              value={selectedLedger?.id || ""}
              onChange={(e) =>
                setSelectedLedger(
                  branchLedgers.find(
                    (l) => l.id === Number(e.target.value)
                  )
                )
              }
            >
              <option value="">Select Ledger</option>
              {branchLedgers.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.ledger.name}
                </option>
              ))}
            </select>
          </div>

          <div className="journal-form-input-fields">
            <label>Amount</label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: e.target.value })
              }
            />
          </div>
        </div>

        <button type="button" onClick={handleAddOrUpdateEntry}>
          {editingIndex !== null ? "Update Entry" : "Add Entry"}
        </button>

<div className="journal-entry-container">

        {entries.length > 0 && (
          <table className="journal-entry-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Ledger</th>
                <th>Debit</th>
                <th>Credit</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{e.ledgerName}</td>
                  <td>{e.debit}</td>
                  <td>{e.credit}</td>
                  <td>
                    <button onClick={() => handleEdit(e, i)}>
                      <Edit />
                    </button>
                    <button onClick={() => handleDelete(i)}>
                      <Trash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>

        <div className="journal-narration-input">
          <label>Narration</label>
          <textarea
            required
            value={formData.narration}
            onChange={(e) =>
              setFormData({ ...formData, narration: e.target.value })
            }
          />
        </div>

<div className="journal-form-submit-btn">
        <button type="submit">Save Journal</button>
</div>
      </form>
    </div>
  );
};

export default Journal;
